import { useRef, useState, type PointerEvent, type TouchEvent } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Activity,
  Briefcase,
  CalendarDays,
  FileSpreadsheet,
  LayoutDashboard,
  MoreHorizontal,
  Sparkles,
  PlugZap,
  Users,
} from 'lucide-react';
import { cn } from '@shared';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/Sheet';

const mobileItems = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/daysheetly', icon: CalendarDays, label: 'Day Sheetly' },
  { to: '/projects', icon: Briefcase, label: 'Projects' },
  { to: '/reports', icon: FileSpreadsheet, label: 'Reports' },
];

/** Primary navigation for touch devices. The desktop sidebar remains the source of truth. */
export function MobileBottomNav() {
  const location = useLocation();
  const { activeWorkspaceId } = useWorkspace();
  const [moreOpen, setMoreOpen] = useState(false);
  const dragStartY = useRef<number | null>(null);

  const handleSheetPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    dragStartY.current = event.clientY;
    if (typeof event.currentTarget.setPointerCapture === 'function') {
      event.currentTarget.setPointerCapture(event.pointerId);
    }
  };

  const handleSheetPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (dragStartY.current !== null && event.clientY - dragStartY.current > 64) {
      setMoreOpen(false);
      dragStartY.current = null;
    }
  };

  const handleSheetPointerUp = (event: PointerEvent<HTMLDivElement>) => {
    if (dragStartY.current !== null && event.clientY - dragStartY.current > 64) {
      setMoreOpen(false);
    }
    dragStartY.current = null;
  };

  const handleSheetTouchStart = (event: TouchEvent<HTMLDivElement>) => {
    dragStartY.current = event.touches[0]?.clientY ?? null;
  };

  const handleSheetTouchMove = (event: TouchEvent<HTMLDivElement>) => {
    const currentY = event.touches[0]?.clientY;
    if (dragStartY.current !== null && currentY !== undefined && currentY - dragStartY.current > 64) {
      setMoreOpen(false);
      dragStartY.current = null;
    }
  };

  const handleSheetTouchEnd = (event: TouchEvent<HTMLDivElement>) => {
    const currentY = event.changedTouches[0]?.clientY;
    if (dragStartY.current !== null && currentY !== undefined && currentY - dragStartY.current > 64) {
      setMoreOpen(false);
    }
    dragStartY.current = null;
  };

  const membersPath = activeWorkspaceId
    ? `/workspace/${activeWorkspaceId}/settings/members`
    : '/onboarding/workspace';

  const moreItems = [
    { to: membersPath, icon: Users, label: 'Members' },
    { to: '/settings/activity-assistant', icon: Activity, label: 'Activity Assistant' },
    { to: '/settings/subscription', icon: Sparkles, label: 'Subscription' },
    { to: '/settings/integrations/mcp', icon: PlugZap, label: 'AI Agents & MCP' },
  ];

  const isMoreActive =
    location.pathname.startsWith('/workspace/') ||
    location.pathname === '/settings/activity-assistant' ||
    location.pathname === '/settings/subscription' ||
    location.pathname === '/settings/integrations/mcp';

  return (
    <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
      <nav
        aria-label="Primary navigation"
        data-testid="mobile-bottom-nav"
        className="mobile-bottom-nav pointer-events-none fixed inset-x-0 bottom-0 z-40 border-t border-border bg-background/95 backdrop-blur lg:hidden"
      >
        <div className="pointer-events-auto mx-auto grid h-16 max-w-lg grid-cols-5 px-1">
          {mobileItems.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              aria-label={label}
              className={({ isActive }) =>
                cn(
                  'flex min-w-0 flex-col items-center justify-center gap-1 rounded-md px-1 text-[11px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
                  isActive ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
                )
              }
            >
              <Icon className="h-5 w-5" aria-hidden="true" />
              <span className="max-w-full truncate">{label}</span>
            </NavLink>
          ))}

          <SheetTrigger asChild>
            <button
              type="button"
              aria-label="More"
              data-testid="mobile-more-trigger"
              className={cn(
                'flex min-w-0 flex-col items-center justify-center gap-1 rounded-md px-1 text-[11px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
                isMoreActive || moreOpen
                  ? 'text-primary'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <MoreHorizontal className="h-5 w-5" aria-hidden="true" />
              <span className="max-w-full truncate">More</span>
            </button>
          </SheetTrigger>
        </div>
      </nav>

      <SheetContent
        side="bottom"
        aria-describedby="mobile-more-description"
        className="max-h-[82vh] rounded-t-3xl px-4 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-3 sm:mx-auto sm:max-w-lg"
      >
        <div
          className="mx-auto mb-3 h-1.5 w-12 touch-none rounded-full bg-muted-foreground/30"
          aria-hidden="true"
          onPointerDown={handleSheetPointerDown}
          onPointerMove={handleSheetPointerMove}
          onPointerUp={handleSheetPointerUp}
          onPointerCancel={() => {
            dragStartY.current = null;
          }}
          onTouchStart={handleSheetTouchStart}
          onTouchMove={handleSheetTouchMove}
          onTouchEnd={handleSheetTouchEnd}
          onTouchCancel={() => {
            dragStartY.current = null;
          }}
        />
        <SheetTitle className="text-2xl font-semibold tracking-tight">More</SheetTitle>
        <SheetDescription id="mobile-more-description" className="sr-only">
          Additional workspace and application options
        </SheetDescription>

        <div className="mt-4 overflow-hidden rounded-2xl border bg-card">
          {moreItems.map(({ to, icon: Icon, label }) => (
            <SheetClose asChild key={to}>
              <NavLink
                to={to}
                className="flex min-h-14 items-center gap-3 border-b px-3 py-3 text-sm font-medium transition-colors last:border-b-0 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Icon className="h-5 w-5" aria-hidden="true" />
                </span>
                <span>{label}</span>
              </NavLink>
            </SheetClose>
          ))}
        </div>

      </SheetContent>
    </Sheet>
  );
}
